/**
 * Rate Limiter Service
 * Prevents spam and bot flooding by implementing various rate limiting strategies
 */

import { appConfig } from "../config/appConfig";

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  blockDurationMs: number;
}

interface RateLimitEntry {
  count: number;
  firstRequest: number;
  blockedUntil?: number;
}

class RateLimiterService {
  private storage: Map<string, RateLimitEntry> = new Map();
  private config: RateLimitConfig;

  constructor(
    config: RateLimitConfig = {
      maxRequests: 10, // Max 10 requests
      windowMs: 60000, // Per minute
      blockDurationMs: 300000, // Block for 5 minutes if exceeded
    }
  ) {
    this.config = config;

    // Clean up old entries every 5 minutes
    setInterval(() => this.cleanup(), 300000);
  }

  /**
   * Check if a request should be allowed based on rate limiting
   */
  isAllowed(identifier: string): {
    allowed: boolean;
    remaining: number;
    resetTime: number;
  } {
    const now = Date.now();
    const entry = this.storage.get(identifier);

    // If user is currently blocked
    if (entry?.blockedUntil && now < entry.blockedUntil) {
      return {
        allowed: false,
        remaining: 0,
        resetTime: entry.blockedUntil,
      };
    }

    // If no entry exists, create one
    if (!entry) {
      this.storage.set(identifier, {
        count: 1,
        firstRequest: now,
      });
      return {
        allowed: true,
        remaining: this.config.maxRequests - 1,
        resetTime: now + this.config.windowMs,
      };
    }

    // Check if window has expired
    if (now - entry.firstRequest > this.config.windowMs) {
      // Reset the window
      this.storage.set(identifier, {
        count: 1,
        firstRequest: now,
      });
      return {
        allowed: true,
        remaining: this.config.maxRequests - 1,
        resetTime: now + this.config.windowMs,
      };
    }

    // Check if limit exceeded
    if (entry.count >= this.config.maxRequests) {
      // Block the user
      this.storage.set(identifier, {
        ...entry,
        blockedUntil: now + this.config.blockDurationMs,
      });
      return {
        allowed: false,
        remaining: 0,
        resetTime: now + this.config.blockDurationMs,
      };
    }

    // Increment count
    this.storage.set(identifier, {
      ...entry,
      count: entry.count + 1,
    });

    return {
      allowed: true,
      remaining: this.config.maxRequests - entry.count - 1,
      resetTime: entry.firstRequest + this.config.windowMs,
    };
  }

  /**
   * Get rate limit status for an identifier
   */
  getStatus(identifier: string): {
    count: number;
    remaining: number;
    resetTime: number;
    isBlocked: boolean;
  } {
    const now = Date.now();
    const entry = this.storage.get(identifier);

    if (!entry) {
      return {
        count: 0,
        remaining: this.config.maxRequests,
        resetTime: now + this.config.windowMs,
        isBlocked: false,
      };
    }

    const isBlocked = entry.blockedUntil ? now < entry.blockedUntil : false;
    const windowExpired = now - entry.firstRequest > this.config.windowMs;
    const remaining = windowExpired
      ? this.config.maxRequests
      : Math.max(0, this.config.maxRequests - entry.count);

    return {
      count: windowExpired ? 0 : entry.count,
      remaining,
      resetTime: windowExpired
        ? now + this.config.windowMs
        : entry.firstRequest + this.config.windowMs,
      isBlocked,
    };
  }

  /**
   * Reset rate limit for an identifier (admin function)
   */
  reset(identifier: string): void {
    this.storage.delete(identifier);
  }

  /**
   * Clean up old entries to prevent memory leaks
   */
  private cleanup(): void {
    const now = Date.now();
    const maxAge = this.config.windowMs + this.config.blockDurationMs;

    const entriesToDelete: string[] = [];
    this.storage.forEach((entry, identifier) => {
      const isExpired = now - entry.firstRequest > maxAge;
      const isBlockedAndExpired =
        entry.blockedUntil && now > entry.blockedUntil;

      if (isExpired || isBlockedAndExpired) {
        entriesToDelete.push(identifier);
      }
    });

    entriesToDelete.forEach((identifier) => {
      this.storage.delete(identifier);
    });
  }

  /**
   * Get all active rate limit entries (for monitoring)
   */
  getAllEntries(): Array<{ identifier: string; entry: RateLimitEntry }> {
    const entries: Array<{ identifier: string; entry: RateLimitEntry }> = [];
    this.storage.forEach((entry, identifier) => {
      entries.push({ identifier, entry });
    });
    return entries;
  }
}

// Create different rate limiters for different use cases
export const chatRateLimiter = new RateLimiterService({
  maxRequests: appConfig.security.rateLimiting.chat.maxRequests,
  windowMs: appConfig.security.rateLimiting.chat.windowMs,
  blockDurationMs: appConfig.security.rateLimiting.chat.blockDurationMs,
});

export const emailRateLimiter = new RateLimiterService({
  maxRequests: appConfig.security.rateLimiting.email.maxRequests,
  windowMs: appConfig.security.rateLimiting.email.windowMs,
  blockDurationMs: appConfig.security.rateLimiting.email.blockDurationMs,
});

export const generalRateLimiter = new RateLimiterService({
  maxRequests: 20, // 20 requests per minute
  windowMs: 60000, // 1 minute
  blockDurationMs: 600000, // Block for 10 minutes
});

export default RateLimiterService;
